import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { ErrorHandler } from '../utils/errorHandler';
import { isAuthSessionProbe, shouldHardRedirectToLogin } from './authRedirect';
import { attachCsrfHeader } from './csrfHeaders';

// Determine a safe base URL to avoid mixed-content when the app is served over HTTPS.
// - In development, default to the backend on http://localhost:5000 unless overridden
//   by REACT_APP_API_URL.
// - In production, default to same-origin (empty baseURL) so that requests like
//   "/api/..." go to the current origin (http or https), which is then proxied by nginx.
const isProduction = process.env.NODE_ENV === 'production';
const resolvedBaseUrl = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return isProduction ? '' : 'http://localhost:5000';
})();

const api = axios.create({
  baseURL: resolvedBaseUrl,
  withCredentials: true,
});

const CSRF_HEADER = 'x-csrf-token';
let csrfRequestPromise: Promise<string | null> | null = null;

const fetchCsrfToken = async (): Promise<string | null> => {
  if (csrfRequestPromise) return csrfRequestPromise;

  const csrfEndpoint = resolvedBaseUrl
    ? `${resolvedBaseUrl}/api/auth/csrf-token`
    : '/api/auth/csrf-token';

  csrfRequestPromise = axios
    .get(csrfEndpoint, { withCredentials: true })
    .then((response) => response.data?.csrfToken ?? null)
    .catch(() => null)
    .finally(() => {
      csrfRequestPromise = null;
    });

  return csrfRequestPromise;
};

// Utility function to convert object keys from camelCase to snake_case recursively
const toSnakeCase = (data: any): any => {
  return snakecaseKeys(data, { deep: true });
};

// Utility function to convert object keys from snake_case to camelCase recursively
const toCamelCase = (data: any): any => {
  return camelcaseKeys(data, { deep: true });
};

api.interceptors.request.use(
  async (config) => {
    const method = (config.method || 'get').toLowerCase();
    const isMutating = ['post', 'put', 'patch', 'delete'].includes(method);
    const isCsrfExempt =
      config.url?.includes('/api/auth/login') ||
      config.url?.includes('/api/auth/csrf-token');

    if (isMutating && !isCsrfExempt) {
      const csrfToken = await fetchCsrfToken();

      if (csrfToken) {
        config.headers = attachCsrfHeader(config.headers, CSRF_HEADER, csrfToken);
      }
    }

    if (config.data && config.url) {
      const url = config.url;
      let transformedData = config.data; // Start with original data

      // Special case: vineyard-maintenance and fermentation-maintenance (send as-is)
      // Aligning fermentation-maintenance with response interceptor logic.
      if (url.includes('vineyard-maintenance') || url.includes('fermentation-maintenance')) {
      }
      // Endpoints requiring snake_case (e.g., fermentation)
      else if (url.includes('/api/fermentation')) {
        transformedData = toSnakeCase(transformedData);
      }
      // Endpoints expecting camelCase (default - no transformation needed)
      // Includes /api/users, /api/auth/register, /api/vineyard, /api/vessel, /api/post-fermentation etc.
      else {
        // Data remains as transformedData (which is original config.data - assumed camelCase from client)
      }

      config.data = transformedData;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// Track whether a token refresh is in-flight to avoid duplicate refresh requests
let isRefreshing = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];

const subscribeToRefresh = (cb: (success: boolean) => void) => {
  refreshSubscribers.push(cb);
};

const notifyRefreshSubscribers = (success: boolean) => {
  refreshSubscribers.forEach(cb => cb(success));
  refreshSubscribers = [];
};

const attemptTokenRefresh = async (): Promise<boolean> => {
  const refreshEndpoint = resolvedBaseUrl
    ? `${resolvedBaseUrl}/api/auth/refresh`
    : '/api/auth/refresh';

  try {
    await axios.post(refreshEndpoint, {}, { withCredentials: true });
    return true;
  } catch {
    return false;
  }
};

const redirectToLoginIfNeeded = (url?: string): boolean => {
  if (typeof window === 'undefined') return false;
  return shouldHardRedirectToLogin(window.location.pathname, url);
};

// Add a response interceptor to convert snake_case to camelCase and handle errors
api.interceptors.response.use(
  response => {
    if (process.env.NODE_ENV === 'development') {
      // Skip case conversion for endpoints containing fermentation-maintenance to preserve exact server response
      if (response.config.url?.includes('fermentation-maintenance')) {
        return response;
      }

      // Apply case conversion for other endpoints
      if (typeof response.data === 'object' && response.data !== null) {
        response.data = toCamelCase(response.data);
      }
    }

    return response;
  },
  async error => {
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const { status } = error.response || {};
    const originalRequest = error.config;

    if (status === 401 && isAuthSessionProbe(originalRequest?.url)) {
      return Promise.reject(error);
    }

    // On 401, attempt a token refresh once before redirecting to login
    if (
      status === 401 &&
      !originalRequest._retried &&
      !originalRequest.url?.includes('/api/auth/refresh') &&
      !originalRequest.url?.includes('/api/auth/login') &&
      !isAuthSessionProbe(originalRequest.url)
    ) {
      originalRequest._retried = true;

      if (isRefreshing) {
        // Another refresh is already in flight — queue this request
        return new Promise((resolve, reject) => {
          subscribeToRefresh((success) => {
            if (success) {
              resolve(api(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      isRefreshing = true;
      const refreshed = await attemptTokenRefresh();
      isRefreshing = false;
      notifyRefreshSubscribers(refreshed);

      if (refreshed) {
        return api(originalRequest);
      }

      // Refresh failed — redirect to login (never from public marketing pages)
      if (redirectToLoginIfNeeded(originalRequest.url)) {
        window.location.href = '/login';
      }

      ErrorHandler.logError(error, 'API Request');
      return Promise.reject(error);
    }

    if (status === 401 && redirectToLoginIfNeeded(originalRequest?.url)) {
      const isDashboardCall = error.config?.url?.includes('/api/batch') ||
        error.config?.url?.includes('/api/vineyard') ||
        error.config?.url?.includes('/api/vessel') ||
        error.config?.url?.includes('/api/fermentation') ||
        error.config?.url?.includes('/api/aging') ||
        error.config?.url?.includes('/api/bottling');

      if (!isDashboardCall) {
        window.location.href = '/login';
      }
    }

    // Use standardized error logging
    ErrorHandler.logError(error, 'API Request');
    return Promise.reject(error);
  }
);

export default api;
